import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { User, UserData } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, fromEvent, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { Resource, ResourceData } from './resource.model';
import { ResourceService } from './resource.service';

export interface LiveEditorActiveResult {
    resource: Resource | null;
    editors: User[];
}

/**
 * Field locking only runs while another editor is on the same resource:
 * Enter/focus → POST dominate `{ field }`
 * Leave/exit  → PUT `{ field, value }` only when value changed (latest value)
 *
 * Disabled when the editor list is empty or only the current user is present.
 * Composer save then owns every dirty field.
 */
@Injectable({ providedIn: 'root' })
export class LiveEditorFieldLockService implements OnDestroy {
    private resourceId: number | null = null;
    private resourceForm: FormGroup | null = null;
    private currentUserId: number | null = null;
    private localField: string | null = null;
    private resumeField: string | null = null;
    /** Value when the field was dominated — used to detect real changes. */
    private baselineValue: unknown = undefined;
    private trackingSub: Subscription | null = null;
    private requestSub: Subscription | null = null;
    /** Composer save owns persistence — skip the delayed leave-field PUT that races it. */
    private suppressLeaveSave = false;
    /** Last editor list, re-applied once currentUserId is known. */
    private pendingEditors: User[] | null = null;
    private readonly locks$ = new BehaviorSubject<ReadonlyMap<string, User>>(new Map());
    private readonly hasOtherEditors$$ = new BehaviorSubject<boolean>(false);
    private readonly resourcePatch$ = new Subject<Resource>();
    private readonly disabledByLock = new Set<string>();

    constructor(
        private readonly ngZone: NgZone,
        private readonly resourceService: ResourceService,
        private readonly auth: AuthService
    ) {
        this.auth
            .getUser()
            .pipe(first())
            .subscribe((user) => {
                this.currentUserId = user?.id ?? null;
                if (this.pendingEditors) {
                    const pending = this.pendingEditors;
                    this.pendingEditors = null;
                    this.applyEditors(pending);
                }
            });
    }

    get otherEditorsLocks$(): Observable<ReadonlyMap<string, User>> {
        return this.locks$.asObservable();
    }

    get resourcePatches$(): Observable<Resource> {
        return this.resourcePatch$.asObservable();
    }

    /** True while at least one editor other than the current user is on the resource. */
    get hasOtherEditors(): boolean {
        return this.hasOtherEditors$$.getValue();
    }

    get hasOtherEditors$(): Observable<boolean> {
        return this.hasOtherEditors$$.asObservable().pipe(distinctUntilChanged());
    }

    isActive(): boolean {
        return !!this.resourceId && !!this.resourceForm && this.hasOtherEditors;
    }

    getDominatedField(): string | null {
        return this.localField;
    }

    getDominatedBy(field: string): User | null {
        return this.locks$.getValue().get(field) ?? null;
    }

    getDominatedHint(field: string): string | null {
        const editor = this.getDominatedBy(field);
        return editor?.name
            ? $localize`:@@fieldDominatedByEditor:${editor.name}:editorName: is editing this field`
            : null;
    }

    getFormControl(field: string): AbstractControl | null {
        return this.resourceForm?.get(field) ?? null;
    }

    resolvePathFromElement(target: EventTarget | null): string | null {
        return this.resolveControlPath(target);
    }

    start(resourceId: number, resourceForm: FormGroup): void {
        if (!resourceId || !resourceForm) {
            return;
        }
        // Do not require trackingSub — when you're the only editor tracking is off.
        if (this.resourceId === resourceId && this.resourceForm === resourceForm) {
            return;
        }

        this.stop();
        this.resourceId = resourceId;
        this.resourceForm = resourceForm;
        this.suppressLeaveSave = false;
    }

    /**
     * Manual/auto composer save is about to persist every dirty control.
     * Cancel in-flight field PUTs and ignore the blur leave that clicking Save produces.
     */
    onComposerSave(): void {
        this.suppressLeaveSave = true;
        this.requestSub?.unsubscribe();
        this.requestSub = null;
        this.releaseLocalField();
    }

    stop(): void {
        const field = this.localField;
        const resourceId = this.resourceId;

        this.trackingSub?.unsubscribe();
        this.trackingSub = null;
        this.requestSub?.unsubscribe();
        this.requestSub = null;

        if (field && resourceId && this.hasOtherEditors) {
            const latest = this.readLatestValue(field);
            if (this.hasValueChanged(latest)) {
                this.resourceService
                    .saveLiveEditorField(resourceId, field, latest)
                    .pipe(catchError(() => EMPTY))
                    .subscribe();
            }
        }

        this.clearForeignLocks();
        this.releaseLocalField();
        this.suppressLeaveSave = false;
        this.pendingEditors = null;
        this.resourceId = null;
        this.resourceForm = null;
        this.locks$.next(new Map());
        this.hasOtherEditors$$.next(false);
    }

    applyEditors(editors: User[]): void {
        // Until we know who "me" is, never treat the list as collaboration — that would
        // dominate fields while you're the only editor.
        if (this.currentUserId == null) {
            this.pendingEditors = editors ?? [];
            this.setCollaborative(false);
            return;
        }

        const others = (editors ?? []).filter((editor) => !this.isCurrentUser(editor));
        this.setCollaborative(!!others.length, others);
    }

    private setCollaborative(isCollaborative: boolean, others: User[] = []): void {
        const wasCollaborative = this.hasOtherEditors;

        const locks = new Map<string, User>();
        for (const editor of others) {
            if (editor.dominated_field) {
                locks.set(editor.dominated_field, editor);
            }
        }

        this.hasOtherEditors$$.next(isCollaborative);
        this.locks$.next(locks);
        this.syncDisabledControls(locks);

        if (isCollaborative && !wasCollaborative) {
            this.bindFocusTracking();
            this.dominateFocusedField();
        } else if (!isCollaborative && wasCollaborative) {
            this.trackingSub?.unsubscribe();
            this.trackingSub = null;
            this.requestSub?.unsubscribe();
            this.requestSub = null;
            this.clearForeignLocks();
            this.releaseLocalField();
        }
    }

    private isCurrentUser(editor: User): boolean {
        return editor?.id != null && this.currentUserId != null && Number(editor.id) === Number(this.currentUserId);
    }

    onIdle(): void {
        const field = this.localField;
        if (!field || !this.resourceId || !this.hasOtherEditors) {
            return;
        }

        // Keep field for POST /active reclaim after idle.
        this.resumeField = field;
        const resourceId = this.resourceId;
        const latest = this.readLatestValue(field);
        const changed = this.hasValueChanged(latest);
        const control = this.resourceForm?.get(field);

        this.localField = null;
        this.baselineValue = undefined;
        this.requestSub?.unsubscribe();

        // Persist unsaved edits before server idle clears dominate.
        if (changed) {
            this.requestSub = this.resourceService
                .saveLiveEditorField(resourceId, field, latest)
                .pipe(
                    tap((editors) => {
                        this.applyEditors(editors);
                        control?.markAsPristine();
                    }),
                    catchError(() => EMPTY)
                )
                .subscribe();
        }
    }

    /**
     * After idle: POST /active with `{ field }` only — clear idle and reclaim, no value save.
     */
    resumeActive(): Observable<LiveEditorActiveResult | null> {
        const resourceId = this.resourceId;
        const field = this.resolveControlPath(document.activeElement) ?? this.resumeField;
        if (!resourceId || !field || !this.hasOtherEditors) {
            return of(null);
        }

        return this.resourceService.notifyLiveEditorIsActive(resourceId, { field }).pipe(
            tap(({ resource, editors }) => {
                this.localField = field;
                this.baselineValue = this.cloneValue(this.readLatestValue(field));
                this.resumeField = null;
                this.applyEditors(editors);
                if (resource) {
                    this.resourcePatch$.next(resource);
                }
            }),
            catchError((err: unknown) => {
                this.handleActiveConflict(err);
                return of(null);
            })
        );
    }

    ngOnDestroy(): void {
        this.stop();
    }

    /** A second editor arrived: claim whatever the user is typing in right now. */
    private dominateFocusedField(): void {
        if (!this.resourceId || !this.hasOtherEditors) {
            return;
        }
        const field = this.resolveControlPath(document.activeElement);
        if (field && !this.locks$.getValue().has(field)) {
            this.dominate(field);
        }
    }

    /** Back to a single editor: forget lock bookkeeping, the composer save persists the form. */
    private releaseLocalField(): void {
        this.localField = null;
        this.resumeField = null;
        this.baselineValue = undefined;
    }

    private handleActiveConflict(err: unknown): void {
        this.localField = null;
        this.resumeField = null;
        this.baselineValue = undefined;

        if (!(err instanceof HttpErrorResponse) || err.status !== 409) {
            return;
        }

        const data = ((err.error as Record<string, unknown>)?.['data'] ?? err.error) as Record<string, unknown> | null;
        if (Array.isArray(data?.['editors'])) {
            this.applyEditors(this.resourceService.mapResourceEditors(data['editors'] as UserData[]));
        }
        if (data?.['resource'] && typeof data['resource'] === 'object') {
            this.resourcePatch$.next(this.resourceService.createResource(data['resource'] as ResourceData));
        }
    }

    private bindFocusTracking(): void {
        if (this.trackingSub) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            const enter$ = fromEvent<FocusEvent>(document, 'focusin').pipe(
                map((e) => this.resolveControlPath(e.target)),
                filter((path): path is string => !!path),
                debounceTime(50),
                tap((path) => this.ngZone.run(() => this.enterField(path)))
            );

            const leave$ = fromEvent<FocusEvent>(document, 'focusout').pipe(
                // Let Angular commit the last keystroke into the FormControl before we read it.
                debounceTime(150),
                filter(
                    () =>
                        !this.suppressLeaveSave &&
                        !!this.localField &&
                        !this.resolveControlPath(document.activeElement)
                ),
                tap(() => this.ngZone.run(() => this.leaveField(this.localField, null)))
            );

            this.trackingSub = merge(enter$, leave$).subscribe();
        });
    }

    /** Focus → POST dominate `{ field }` and remember baseline value. */
    private enterField(field: string): void {
        this.suppressLeaveSave = false;
        if (
            !this.resourceId ||
            !this.hasOtherEditors ||
            this.locks$.getValue().has(field) ||
            this.localField === field
        ) {
            return;
        }

        const previous = this.localField;
        if (previous) {
            this.leaveField(previous, field);
            return;
        }

        this.dominate(field);
    }

    /**
     * Leave/exit → PUT `{ field, latestValue }` only when value changed.
     * Then optionally POST-dominate `nextField`.
     */
    private leaveField(field: string | null, nextField: string | null): void {
        if (this.suppressLeaveSave || !this.hasOtherEditors) {
            this.releaseLocalField();
            return;
        }

        if (!field || !this.resourceId) {
            if (nextField) {
                this.dominate(nextField);
            }
            return;
        }

        const resourceId = this.resourceId;
        const latest = this.readLatestValue(field);
        const changed = this.hasValueChanged(latest);
        const control = this.resourceForm?.get(field);
        this.requestSub?.unsubscribe();

        const dominateNext$ = nextField
            ? this.resourceService.lockLiveEditorField(resourceId, nextField).pipe(
                  tap((editors) => {
                      this.localField = nextField;
                      this.baselineValue = this.cloneValue(this.readLatestValue(nextField));
                      this.resumeField = null;
                      this.applyEditors(editors);
                  }),
                  catchError(() => {
                      this.localField = null;
                      this.baselineValue = undefined;
                      return EMPTY;
                  })
              )
            : of(null).pipe(
                  tap(() => {
                      this.localField = null;
                      this.baselineValue = undefined;
                  })
              );

        // PUT only when the control value actually changed — always send latest.
        const leave$ = changed
            ? this.resourceService.saveLiveEditorField(resourceId, field, latest).pipe(
                  tap((editors) => {
                      this.applyEditors(editors);
                      control?.markAsPristine();
                  }),
                  catchError(() => EMPTY),
                  switchMap(() => dominateNext$)
              )
            : dominateNext$;

        if (!nextField) {
            this.localField = null;
            this.baselineValue = undefined;
        }
        this.requestSub = leave$.subscribe();
    }

    private dominate(field: string): void {
        if (!this.resourceId || !this.hasOtherEditors) {
            return;
        }

        this.localField = field;
        this.baselineValue = this.cloneValue(this.readLatestValue(field));
        this.resumeField = null;
        this.requestSub?.unsubscribe();
        this.requestSub = this.resourceService
            .lockLiveEditorField(this.resourceId, field)
            .pipe(
                tap((editors) => this.applyEditors(editors)),
                catchError(() => {
                    this.localField = null;
                    this.baselineValue = undefined;
                    return EMPTY;
                })
            )
            .subscribe();
    }

    /**
     * Latest value from the live DOM input (if present), synced into the FormControl.
     * Avoids PUT sending a stale FormControl value on blur.
     * Chip/CVA controls hold arrays/objects — never overwrite those with a nested text input.
     */
    private readLatestValue(field: string): unknown {
        const control = this.resourceForm?.get(field);
        const controlValue = control?.value;
        if (controlValue !== null && controlValue !== undefined && typeof controlValue === 'object') {
            return this.cloneValue(controlValue);
        }

        const el = this.findFieldElement(field);
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
            const domValue = el.value;
            if (control && !Utils.isEqual(control.value, domValue)) {
                control.setValue(domValue, { emitEvent: false });
                control.markAsDirty();
            }
            return domValue;
        }

        return controlValue;
    }

    private findFieldElement(field: string): HTMLElement | null {
        const leaf = field.includes('.') ? field.split('.').pop()! : field;
        const candidates = Array.from(
            document.querySelectorAll<HTMLElement>(`[formcontrolname="${leaf}"], [appliveeditorfield="${field}"]`)
        );
        // Prefer an element whose resolved path matches the full field path.
        return (
            candidates.find((el) => this.resolveControlPath(el) === field) ??
            candidates[0] ??
            null
        );
    }

    private hasValueChanged(latest: unknown): boolean {
        // No dominate baseline (already left) — nothing to save.
        if (this.localField == null && this.baselineValue === undefined) {
            return false;
        }
        return !Utils.isEqual(latest, this.baselineValue);
    }

    private cloneValue(value: unknown): unknown {
        if (value == null || typeof value !== 'object') {
            return value;
        }
        try {
            return JSON.parse(JSON.stringify(value));
        } catch {
            return value;
        }
    }

    private syncDisabledControls(locks: ReadonlyMap<string, User>): void {
        const form = this.resourceForm;
        if (!form) {
            return;
        }

        for (const path of [...this.disabledByLock]) {
            if (!locks.has(path)) {
                form.get(path)?.enable({ emitEvent: false });
                this.disabledByLock.delete(path);
            }
        }

        locks.forEach((_, path) => {
            const control = form.get(path);
            if (!control || this.disabledByLock.has(path)) {
                return;
            }
            if (control.enabled) {
                control.disable({ emitEvent: false });
            }
            this.disabledByLock.add(path);
        });
    }

    private clearForeignLocks(): void {
        this.disabledByLock.forEach((path) => this.resourceForm?.get(path)?.enable({ emitEvent: false }));
        this.disabledByLock.clear();
    }

    private resolveControlPath(target: EventTarget | null): string | null {
        if (!(target instanceof HTMLElement) || !this.resourceForm) {
            return null;
        }

        const explicit =
            target.getAttribute('appliveeditorfield') ||
            target.closest('[appliveeditorfield]')?.getAttribute('appliveeditorfield');
        if (explicit && this.resourceForm.get(explicit)) {
            return explicit;
        }

        const segments: string[] = [];
        let current: HTMLElement | null = target;

        while (current) {
            const controlName = current.getAttribute('formcontrolname');
            const groupName = current.getAttribute('formgroupname');
            const arrayName = current.getAttribute('formarrayname');

            if (controlName) {
                segments.unshift(controlName);
            } else if (groupName) {
                segments.unshift(groupName);
            } else if (arrayName) {
                segments.unshift(arrayName);
            }

            if (current.tagName === 'FORM' || current.hasAttribute('formgroup')) {
                break;
            }
            current = current.parentElement;
        }

        const path = segments.join('.');
        return path && this.resourceForm.get(path) ? path : null;
    }
}
